import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import axios from '../../config/axiosInstance';
import AddEditClient from './AddEditClient';
import AddEditUser from './AddEditUser';
import UserSessionsModal from './UserSession';
import AddEditGroup from './AddEditGroup';
import ServersTable from './Components/ServersTable';
import ServerControlModal from './Components/ServerControlModal';

import { Table, Button, Space, message, Input, Card, Typography, Form, Row, Col, Popconfirm, Tag, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, UserAddOutlined, UsergroupAddOutlined, EditOutlined, 
  EyeTwoTone, EyeInvisibleOutlined, CloudTwoTone, ClockCircleOutlined, BarChartOutlined,
  ControlOutlined, PauseCircleOutlined, PlayCircleOutlined, PoweroffOutlined, ReloadOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const AdminPanel = () => {
  const navigate = useNavigate();
  // Clients
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientsSearchTerm, setClientsSearchTerm] = useState('');
  const [modalAddEditClientVisible, setModalAddEditClientVisible] = useState(false);
  const [modalClient, setModalClient] = useState(null); 
  // Users
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [modalAddEditUserVisible, setModalAddEditUserVisible] = useState(false);
  const [modalUser, setModalUser] = useState(null); 
  const [modalUserSession, setUserSession] = useState(false);
  // Groups
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [groupsSearchTerm, setGroupsSearchTerm] = useState('');
  const [modalAddEditGroupVisible, setModalAddEditGroupVisible] = useState(false);
  const [modalGroup, setModalGroup] = useState(null); 
  // Settings
  const [form] = Form.useForm();
  const [settingsExist, setSettingsExist] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  // Common
  const [loading, setLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('clients');
  // Servers
  const [serverModalVisible, setServerModalVisible] = useState(false);
  const [serverSelected, setServerSelected] = useState(null);

  const openServerModal = (srv) => { setServerSelected(srv); setServerModalVisible(true); };
  const closeServerModal = () => { setServerModalVisible(false); setServerSelected(null); };

  //  Versions
  // Front-end version comes from REACT_APP_VERSION = $npm_package_version (set in package.json build script)
  const frontendVersion = process.env.REACT_APP_VERSION ?? '0.0.0';
  // Back-end version fetched once at mount
  const [backendVersion, setBackendVersion] = useState(null);

   useEffect(() => {
        axios
          .get('/settings/version')                // your Next.js API route
          .then(({ data }) => setBackendVersion(data.version))
          .catch(() => setBackendVersion(null)); // swallow errors → hide tag
   }, []);

  // --- GETs
  const fetchSavedClients = async () => {
    setLoading(true);
    try {
      const clientsResponse = await axios.get('/clients');
      setClients(clientsResponse.data);
      setFilteredClients(clientsResponse.data);
    } catch (error) {
      message.error('Error fetching Clients');
      console.error('Error fetching Clients:', error);
    } finally {
      setLoading(false);
    }
  };
  const fetchSavedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const usersResponse = await axios.get('/users');
      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
    } catch (error) {
      message.error('Error fetching Users');
      console.error('Error fetching Users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSavedGroups = async () => {
    setLoading(true);
    try {
      const groupsResponse = await axios.get('/groups');
      setGroups(groupsResponse.data);
      setFilteredGroups(groupsResponse.data);
    } catch (error) {
      message.error('Error fetching Groups');
      console.error('Error fetching Groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const { data } = await axios.get('/settings');
      if (data) {
        form.setFieldsValue(data);
        setSettingsExist(true);
      } else {
        setSettingsExist(false);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('Failed to load settings.');
      setSettingsExist(false);
    } finally {
      setSettingsLoading(false);
    }
  }, [form]);
  
  useEffect(() => {
    fetchSettings();
    fetchSavedUsers();
  }, [fetchSettings, fetchSavedUsers]);

  useEffect(() => {
    fetchSavedClients();
    fetchSavedGroups();
  }, []);

  useEffect(() => {
    // Refresh Users every 60 seconds
    fetchSavedUsers();
    const intervalId = setInterval(() => {
      fetchSavedUsers();
    }, 60000);
  
    return () => clearInterval(intervalId);
  }, [fetchSavedUsers]); 

  useEffect(() => {
    setFilteredClients(clients);
  }, [clients]);

  const tabList = [
    { key: 'clients', tab: 'Clients', },
    { key: 'users', tab: 'Users', },
    { key: 'groups', tab: 'Groups', },
    { key: 'settings', tab: 'Settings',},
    { key: 'servers', tab: 'Servers' },
  ];

  const onTabChange = (key) => {
    setActiveTabKey(key);
  };

  //-- Clients
  const handleSearchClients = (e) => {
    const value = e.target.value;
    setClientsSearchTerm(value);

    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredClients(filtered);
  };

  const handleOpenClientModal = (client) => {
    setModalClient(client); 
    setModalAddEditClientVisible(true); 
  };
  
  const handleCloseClientModal = () => {
    setModalClient(null); 
    setModalAddEditClientVisible(false); 
  };

  const handleDeleteClient = async (_id) => {
    setLoading(true);
    try {
      await axios.delete(`/clients/${_id}`);
      message.success('Client deleted successfully');
      fetchSavedClients();
    } catch (error) {
      message.error('Error deleting client');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenClientReport = (record) => {
    navigate(`/client-report`, { state: { client: record } });
  };
  
  const refreshClient = async () => {
    await fetchSavedClients(); 
  };

  const clientsColumns = [
    {
      title: 'Logo',
      dataIndex: 'portalLogo',
      key: 'portalLogo',
      render: (logo) => (
        logo ? (
          <img alt="Company Logo" src={logo} 
            style={{
              maxWidth: '32px',
              maxHeight: '32px',
              objectFit: 'cover',
            }}
          />
        ) : null 
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Portal Name',
      dataIndex: 'portalName',
      key: 'portalName',
      responsive: ['md', 'lg'],
      sorter: (a, b) => a.portalName.localeCompare(b.portalName),
    },
    {
      title: 'Color',
      dataIndex: 'clientColor',
      key: 'clientColor',
      responsive: ['md', 'lg'],
      render: (color) => (
        <div
          style={{
            width: '32px',
            height: '32px',
            backgroundColor: color || '#ffffff', //Uses a default color if `clientColor` is not defined
            borderRadius: '4px', 
            border: '1px', 
            cursor: 'not-allowed', 
          }}
          title={color || 'No color specified'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
            <Button
              icon={<BarChartOutlined />}
              onClick={() => handleOpenClientReport(record)}
            />
            <Button
              icon={<EditOutlined />}
              onClick={() => handleOpenClientModal(record)}
            />
            <Popconfirm
              title="Are you sure you want to delete this group?"
              onConfirm={() => handleDeleteClient(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ color: 'red' }} icon={<DeleteOutlined />} />
            </Popconfirm>
        </Space>
      ),
    },
  ];

  //-- Users
  const handleOpenModal = (user) => {
    setModalUser(user); 
    setModalAddEditUserVisible(true); 
  };
  
  const handleCloseModal = () => {
    setModalUser(null); 
    setModalAddEditUserVisible(false); 
  };
  
  const refreshUsers = async () => {
    await fetchSavedUsers(); 
  };
  
  const handleDeleteUser = async (_id) => {
    setLoading(true);
    try {
      await axios.delete(`/users/${_id}`);
      message.success('User deleted successfully');
      fetchSavedUsers();
    } catch (error) {
      console.error('Error deleting user:', error.message);
      message.error('Error deleting user');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModalUserSession = (user) => {
    setUserSession(user); 
  };

  const handleSearchUsers = (e) => {
    const value = e.target.value;
    setUsersSearchTerm(value);

    const filtered = users.filter(user =>
      user.username.toLowerCase().includes(value.toLowerCase()) ||
      user.firstname.toLowerCase().includes(value.toLowerCase()) ||
      user.lastname.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredUsers(filtered);
  };

  const usersColumns = [
    {
      title: (
        <span>
          <CloudTwoTone />
        </span>
      ),
      dataIndex: 'isOnline',
      key: 'isOnline',
      render: (isOnline) => (
          <span style={{ position: 'relative', display: 'inline-block', width: '15px', height: '15px' }}>
          {/* Background circle (lighter outline) */}
            <span
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: isOnline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                  position: 'absolute',
                  top: 0,
                  left: 0
                }}
            />

            {/* Front circle (active or inactive indicator) */}
            <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: isOnline ? '#10B981' : '#EF4444',
                  position: 'absolute',
                  top: '2.5px',
                  left: '2.5px'
                }}
            />
        </span>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'clientID',
      key: 'clientID',
      render: (clientID) => (
          clientID?.name ? (
            <Tag color={clientID?.clientColor || 'default'}>
              {clientID?.name}
            </Tag>
          ) : (
            <Tag color="red">N/A</Tag>
          )
      ),
      sorter: (a, b) => {
        const nameA = a.clientID?.name?.toLowerCase() || '';
        const nameB = b.clientID?.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB); 
      },
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
    },
    {
      title: 'Firstname',
      dataIndex: 'firstname',
      key: 'firstname',
      responsive: ['md', 'lg'],
      sorter: (a, b) => a.firstname.localeCompare(b.firstname),
    },
    {
      title: 'Lastname',
      dataIndex: 'lastname',
      key: 'lastname',
      responsive: ['md', 'lg'],
      sorter: (a, b) => a.lastname.localeCompare(b.lastname),
    },
    {
      title: 'Group',
      dataIndex: 'groupID', 
      key: 'groupID',
      responsive: ['md', 'lg'],
      render: (groupID) => groupID?.name || 'N/A', 
      sorter: (a, b) => (a.groupID?.name || '').localeCompare(b.groupID?.name || ''),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<ClockCircleOutlined />}
            onClick={() => handleOpenModalUserSession(record)}
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDeleteUser(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ color: 'red' }} icon={<DeleteOutlined />} />
            </Popconfirm>
        </Space>
      ),
    },
  ];

  //-- Groups
  const handleSearchGroups = (e) => {
    const value = e.target.value;
    setGroupsSearchTerm(value);

    const filtered = groups.filter(group =>
      group.name.toLowerCase().includes(value.toLowerCase()) 
    );

    setFilteredGroups(filtered);
  };

  const handleOpenGroupModal = (group) => {
    setModalGroup(group); 
    setModalAddEditGroupVisible(true); 
  };
  
  const handleCloseGroupModal = () => {
    setModalGroup(null); 
    setModalAddEditGroupVisible(false); 
  };
  
  const refreshGroup = async () => {
    await fetchSavedGroups(); 
  };

  const handleDeleteGroup = async (_id) => {
    setLoading(true);
    try {
      await axios.delete(`/groups/${_id}`);
      message.success('Group deleted successfully');
      fetchSavedUsers();
    } catch (error) {
      console.error('Error deleting group:', error.message);
      message.error('Error deleting group');
    } finally {
      setLoading(false);
    }
  };

  const groupsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
            <Button
                icon={<EditOutlined />}
                onClick={() => handleOpenGroupModal(record)}
            />
            <Popconfirm
              title="Are you sure you want to delete this group?"
              onConfirm={() => handleDeleteGroup(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ color: 'red' }} icon={<DeleteOutlined />} />
            </Popconfirm>
            
        </Space>
      ),
    },
  ];
  
  //Settings

  // Method for saving settings
  const saveSettings = async (values) => {
    setSettingsLoading(true);
    try {
      let response;
      if (settingsExist) {
        response = await axios.put('/settings', values); 
      } else {
        response = await axios.post('/settings', values); 
      }

      message.success(response.data.message || 'Settings saved successfully');
      setSettingsExist(true); 
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings.');
    } finally {
      setSettingsLoading(false);
    }
  };
  
  //Front
  const contentList = {
    clients: (
        <div>
          <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
            <Col xs={20} sm={12} md={10} lg={8}>
              <Input
                placeholder="Search by name"
                prefix={<SearchOutlined />}
                value={clientsSearchTerm}
                onChange={handleSearchClients}
              />
            </Col>
            <Col xs={4} sm={24} md={8} lg={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() =>  handleOpenClientModal(null)}
              >
                <span className="hide-text-mobile">Add</span>
              </Button>
            </Col>
          </Row>
          
          <Table
            columns={clientsColumns}
            dataSource={filteredClients}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 'max-content' }}
          />
          <AddEditClient
            visible={modalAddEditClientVisible}
            onClose={handleCloseClientModal}
            client={modalClient} 
            onSave={refreshClient} 
          />
        </div>
      ),
    users: (
      <div>
        <Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
          <Col xs={20} sm={12} md={10} lg={8}>
            <Input
                placeholder="Search by username, firstname or lastname"
                prefix={<SearchOutlined />}
                value={usersSearchTerm}
                onChange={handleSearchUsers}
            />
          </Col>
          <Col xs={4} sm={24} md={8} lg={4}>
            <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => handleOpenModal(null)}
            >
              <span className="hide-text-mobile">Add</span>
            </Button>
          </Col>
          <Col xs={24} sm={24} md={6} lg={12} style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
              <span style={{ position: 'relative', display: 'inline-block', width: '15px', height: '15px' }}>
                <span
                    style={{
                      width: '15px',
                      height: '15px',
                      borderRadius: '50%',
                      backgroundColor: filteredUsers.filter(u => u.isOnline).length > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }}
                />
                <span
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: filteredUsers.filter(u => u.isOnline).length > 0 ? '#10B981' : '#EF4444',
                      position: 'absolute',
                      top: '2.5px',
                      left: '2.5px'
                    }}
                />
              </span>
                      <span>
                Online users: {filteredUsers.filter(user => user.isOnline).length}
              </span>
            </div>
          </Col>
        </Row>
        
        <Table
          columns={usersColumns}
          dataSource={filteredUsers}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
        <AddEditUser
          visible={modalAddEditUserVisible}
          onClose={handleCloseModal}
          user={modalUser} 
          onSave={refreshUsers} 
        />
        <UserSessionsModal
          visible={modalUserSession}
          onClose={() => setUserSession(false)}
          user={modalUserSession}
        />
      </div>
    ),
    groups: (
      <div>
        <Row Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
          <Col xs={20} sm={12} md={10} lg={8}>
            <Input
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              value={groupsSearchTerm}
              onChange={handleSearchGroups}
            />
          </Col>
          <Col xs={4} sm={24} md={8} lg={4}>
            <Button
              type="primary"
              icon={<UsergroupAddOutlined />}
              onClick={() => handleOpenGroupModal(null)}
            >
              <span className="hide-text-mobile">Add</span>
            </Button>
          </Col>
        </Row>

        <Table
          columns={groupsColumns}
          dataSource={filteredGroups}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
        <AddEditGroup
          visible={modalAddEditGroupVisible}
          onClose={handleCloseGroupModal}
          group={modalGroup} 
          onSave={refreshGroup} 
        />
      </div>
    ),
    settings: (
      <div style={{ padding: '10px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={saveSettings}
            initialValues={{ api_user: '', api_password: '', client_id: '' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="api_user"
                  label="API User"
                  rules={[
                    { required: true, message: 'Please input the user!' },
                  ]}
                >
                  <Input placeholder="Enter the API User" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="api_password"
                  label="API Password"
                  rules={[
                    { required: true, message: 'Please input the password!' },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter password"
                    iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Col span={12}>
              <Form.Item
                name="client_id"
                label="Client ID"
                rules={[
                  { required: true, message: 'Please input the client id!' },
                ]}
              >
                <Input placeholder="Enter the Client ID" />
              </Form.Item>
            </Col>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={() => fetchSettings()}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={settingsLoading}>
              Save
            </Button>
          </div>
        </Form>
      </div>
    ),
    servers: (
        <div>
          <ServersTable onOpenServerModal={openServerModal} />
          <ServerControlModal
              visible={serverModalVisible}
              onClose={closeServerModal}
              server={serverSelected}
          />
        </div>
    ),
  };

  return (
    <div style={{
       display: 'flex',
       flexDirection: 'column',
       minHeight: '95vh',
       padding: '24px',
      }}>
      <Card
        style={{ width: '100%' }}
        title={<Title level={2} style={{ color: 'var(--ant-primary-color)' }}>Administration Panel</Title>}
        tabList={tabList}
        activeTabKey={activeTabKey}
        onTabChange={onTabChange}
      >
        {contentList[activeTabKey]}
      </Card>
      {/* ---------- footer ---------- */}
           <div style={{ marginTop: 'auto', textAlign: 'center' }}>
             <Tag color="blue">FrontEnd v{frontendVersion}</Tag>
            {backendVersion && (
              <Tag color="volcano" style={{ marginLeft: 8 }}>
             BackEnd v{backendVersion}
           </Tag>
         )}
       </div>
    </div>
  );
};

export default AdminPanel;
